import { makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getPMRProgramInformationAPI } from "../../../../api/reports-api";

import {
  TH_BG_BAND,
} from "../../reportTableConfig";

/* P&L by Program design tokens */
const NAV = "#0D2137";
const NAV2 = "#1E3A5F";
const HEADER_BG = TH_BG_BAND;
const PAGE_BG = "transparent";
const CARD_BG = "#FFFFFF";
const METRIC_BG = "#E8E8E8";
const BORDER = "#E5E7EB";
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function fmtValue(field) {
  if (field.value == null || field.value === "") return "";
  if (field.type === "currency") {
    return Number(field.value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (field.type === "percent") {
    return `${Number(field.value).toFixed(2)}%`;
  }
  return String(field.value);
}

// "All" means "no filter applied, this is every record" — only true for the default load.
// Once the user has actually filtered and the matched record's own POP dates are null, that
// would be misleading (it isn't "all", it's just missing data), so show blank instead.
function fmtPopRange(start, end, isFiltered) {
  if (!start && !end) return isFiltered ? "" : "All";
  const f = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—");
  return `${f(start)} – ${f(end)}`;
}

/** Reshape the flat PMR API response into the section groupings this page renders. */
function mapProgramInformation(d, isFiltered) {
  const actualsThrough = d.actualThroughMonth >= 1 && d.actualThroughMonth <= 12
    ? `${MONTH_NAMES[d.actualThroughMonth - 1]} ${d.fiscalYear}`
    : "";
  return {
    meta: {
      forecastThrough: d.currentForecastThrough ?? "",
      reportRunOn:     d.reportRunOn ?? "",
      actualsThrough,
    },
    summary: {
      tcv:         d.tcv ?? 0,
      totalFunded: d.totalFunded ?? 0,
    },
    pop: {
      // Never "All": a missing Current POP (no active CLIN today) is shown blank for every
      // filter/year selection.
      currentPop:    fmtPopRange(d.currentPopStart, d.currentPopEnd, true),
      totalPop:      fmtPopRange(d.totalPopStart, d.totalPopEnd),
      contractType:  d.contractType ?? "All",
      projectNumber: d.project ?? "All",
    },
    funding: {
      left: [
        { label: "Ceiling Value",               value: d.ceilingValue,     type: "currency" },
        { label: "POP Value",                   value: d.popValue,        type: "currency", highlight: true },
        { label: "POP Funded Value",            value: d.popFundedValue,  type: "currency" },
        { label: "ITD Revenue",                 value: d.itdRevenue,      type: "currency" },
        { label: "Funding Expended %",          value: d.fundingExpendedPct, type: "percent" },
        { label: "At Risk Funding",              value: d.atRiskFunding,   type: "currency" },
        { label: "Estimated Date of Recompete", value: d.estimatedDateOfRecompete, type: "text" },
      ],
      right: [
        { label: "Current Opt Value",    value: d.currentOptValue,   type: "currency" },
        { label: "Current Opt Funding",  value: d.currentOptFunding, type: "currency" },
        { label: "ITD Revenue",          value: d.itdRevenue,        type: "currency" },
        { label: "Current Opt Expended", value: d.currentOptExpendedPct, type: "percent" },
      ],
    },
    grossProfit: {
      itd: [
        { label: "Contract to Date Profit", value: d.contractToDateProfitPct, type: "percent" },
        { label: "Est End of POP Profit",   value: d.estEndOfPopProfitPct,    type: "percent" },
        { label: "Profit % as Priced",      value: d.profitPctAsPriced,       type: "percent" },
      ],
      ytd: [
        { label: "Contract YTD Profit",              value: d.yearToDateProfitPct, type: "percent" },
        { label: `${d.fiscalYear ?? ""} EAC Profit`.trim(), value: d.eacProfitPct,  type: "percent" },
        { label: "Budgeted Profit",                  value: d.budgetProfitPct,     type: "percent" },
        { label: "Profit Variance",                  value: d.profitVariance,      type: "percent" },
      ],
    },
  };
}

function SectionBar({ title, classes }) {
  return (
    <div className={classes.sectionBar}>
      <span className={classes.sectionBarTitle}>{title}</span>
    </div>
  );
}

function InfoCard({ rows, classes }) {
  return (
    <div className={classes.infoCard}>
      {rows.map((row) => (
        <div key={row.label} className={classes.infoRow}>
          <span className={classes.infoLabel}>{row.label}</span>
          <span className={classes.infoValue}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function DataPanel({ fields, classes, panelClass }) {
  return (
    <div className={`${classes.dataPanel} ${panelClass || ""}`}>
      {fields.map((field) => (
        <div
          key={field.label}
          className={`${classes.dataRow} ${field.highlight ? classes.dataRowHighlight : ""}`}
        >
          <span className={classes.dataLabel}>{field.label}</span>
          <span className={classes.dataValue}>{fmtValue(field)}</span>
        </div>
      ))}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  root: {
    background: PAGE_BG,
    minHeight: "100%",
    fontFamily: FONT,
    fontSize: "13px",
    color: "#334155",
    paddingBottom: "16px",
  },

  pageHeader: {
    background: `linear-gradient(130deg, ${NAV} 0%, ${NAV2} 100%)`,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    marginBottom: "16px",
    boxShadow: "0 2px 12px rgba(13,33,55,0.3)",
  },
  headerTitleText: { fontSize: "16px", fontWeight: 700, letterSpacing: "0.3px" },
  headerBadge: {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: "20px",
    padding: "2px 10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(255,255,255,0.9)",
    marginLeft: "10px",
  },
  headerDate: {
    fontSize: "12px",
    fontWeight: 500,
    color: "rgba(255,255,255,0.75)",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "8px",
    padding: "4px 12px",
  },

  contentCard: {
    background: CARD_BG,
    margin: "0 16px",
    borderRadius: "12px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  sectionBar: {
    background: HEADER_BG,
    color: "#fff",
    textAlign: "center",
    padding: "10px 16px",
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.06em",
  },
  sectionBarTitle: {
    display: "block",
  },

  block: {
    padding: "16px 20px",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    padding: "16px 20px",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },

  infoCard: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  infoRow: {
    display: "flex",
    alignItems: "stretch",
    minHeight: "36px",
    borderBottom: `1px solid ${BORDER}`,
    "&:last-child": { borderBottom: "none" },
  },
  infoLabel: {
    flex: "0 0 48%",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    color: "#334155",
    fontSize: "13px",
    borderRight: `1px solid ${BORDER}`,
  },
  infoValue: {
    flex: 1,
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontWeight: 600,
    fontSize: "13px",
    color: NAV,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  metricCard: {
    background: METRIC_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "72px",
  },
  metricText: {
    fontSize: "18px",
    fontWeight: 700,
    color: NAV,
    textAlign: "center",
    lineHeight: 1.35,
    fontVariantNumeric: "tabular-nums",
  },

  popRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    padding: "16px 20px",
    alignItems: "stretch",
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  projectNum: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "22px",
    fontWeight: 700,
    color: NAV,
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    padding: "24px",
    textAlign: "center",
  },

  subSectionBar: {
    background: HEADER_BG,
    color: "#fff",
    textAlign: "center",
    padding: "8px 12px",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.06em",
  },

  dataPanel: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  dataRow: {
    display: "flex",
    alignItems: "stretch",
    minHeight: "32px",
    borderBottom: `1px solid ${BORDER}`,
    "&:last-child": { borderBottom: "none" },
  },
  dataRowHighlight: {
    background: METRIC_BG,
  },
  dataLabel: {
    flex: "0 0 52%",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    color: "#334155",
    fontSize: "12px",
    borderRight: `1px solid ${BORDER}`,
  },
  dataValue: {
    flex: 1,
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontWeight: 600,
    fontSize: "12px",
    color: NAV,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  gpCol: {
    display: "flex",
    flexDirection: "column",
  },
  gpPanel: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderTop: "none",
  },

  errorText: { padding: "24px", color: "#DC2626", fontSize: 13, fontFamily: FONT },
}));

export default function ProgramInformation({ filters = {}, isFiltered = false, onMeta }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [data,    setData]    = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPMRProgramInformationAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          setData(mapProgramInformation(d, isFiltered));
          onMeta?.({ actualThroughMonth: d.actualThroughMonth, fiscalYear: d.fiscalYear });
        } else {
          setError("Failed to load data.");
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load data."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters), isFiltered]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;
  if (error || !data) return <div className={classes.errorText}>{error || "No data available."}</div>;

  const { meta, summary, pop, funding, grossProfit } = data;

  const metaLeft = [
    { label: "Current Forecast through", value: meta.forecastThrough },
  ];
  const metaRight = [
    { label: "Report Run on", value: meta.reportRunOn },
    { label: "Actuals through", value: meta.actualsThrough },
  ];
  const popRows = [
    { label: "Current POP", value: pop.currentPop },
    { label: "Total POP", value: pop.totalPop },
    { label: "Contract Type", value: pop.contractType },
  ];

  const tcvFormatted = `$${Number(summary.tcv).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  const fundedFormatted = `$${Number(summary.totalFunded).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

  return (
    <div className={classes.root}>
      <div className={classes.contentCard}>
        <div className={classes.twoCol}>
          <InfoCard rows={metaLeft} classes={classes} />
          <InfoCard rows={metaRight} classes={classes} />
        </div>

        <div className={classes.twoCol}>
          <div className={classes.metricCard}>
            <span className={classes.metricText}>TCV: {tcvFormatted}</span>
          </div>
          <div className={classes.metricCard}>
            <span className={classes.metricText}>Total Funded: {fundedFormatted}</span>
          </div>
        </div>

        <SectionBar title="PERIOD OF PERFORMANCE" classes={classes} />
        <div className={classes.popRow}>
          <InfoCard rows={popRows} classes={classes} />
          <div className={classes.projectNum}>Project #: {pop.projectNumber}</div>
        </div>

        <SectionBar title="FUNDING" classes={classes} />
        <div className={classes.twoCol}>
          <DataPanel fields={funding.left} classes={classes} />
          <DataPanel fields={funding.right} classes={classes} />
        </div>

        <SectionBar title="GROSS PROFIT" classes={classes} />
        <div className={classes.twoCol}>
          <div className={classes.gpCol}>
            <div className={classes.subSectionBar}>INCEPTION TO DATE</div>
            <DataPanel fields={grossProfit.itd} classes={classes} panelClass={classes.gpPanel} />
          </div>
          <div className={classes.gpCol}>
            <div className={classes.subSectionBar}>YEAR TO DATE{meta.actualsThrough ? ` (${meta.actualsThrough.split(" ")[1]})` : ""}</div>
            <DataPanel fields={grossProfit.ytd} classes={classes} panelClass={classes.gpPanel} />
          </div>
        </div>

        <div className={classes.block} style={{ paddingBottom: 32 }} />
      </div>
    </div>
  );
}
